import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PencilIcon, TrashIcon } from '@heroicons/react/20/solid';
import { activityApi } from '../../services/api';
import { ROUTES } from '../../utils/constants';
import { formatDate, getErrorMessage } from '../../utils/helpers';
import { sanitizeHtml } from '../../utils/htmlSanitizer';
import { parseMarkdown } from '../../utils/markdownParser';

// Convert markdown to HTML and sanitize
const renderCommentBody = (text) => {
  if (!text) return '';
  const html = parseMarkdown(text);
  return sanitizeHtml(html);
};

function CommentSection({ activityId, comments, currentUser, isAuthenticated }) {
  const [commentsList, setCommentsList] = useState(comments);
  const [newComment, setNewComment] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim() || loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await activityApi.createComment(activityId, {
        body: newComment.trim()
      });
      
      setCommentsList([...commentsList, response.comment]);
      setNewComment('');
    } catch (err) {
      console.error('Failed to add comment:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editText.trim() || loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await activityApi.updateComment(activityId, commentId, {
        body: editText.trim()
      });
      
      setCommentsList(commentsList.map(comment => 
        comment.commentId === commentId ? response.comment : comment
      ));
      setEditingComment(null);
      setEditText('');
    } catch (err) {
      console.error('Failed to update comment:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await activityApi.deleteComment(activityId, commentId);
      setCommentsList(commentsList.filter(comment => comment.commentId !== commentId));
    } catch (err) {
      console.error('Failed to delete comment:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const canEditComment = (comment) => {
    return isAuthenticated && (
      currentUser?.id === comment.authorId || 
      currentUser?.roles?.includes('admin')
    );
  };

  const startEditing = (comment) => {
    setEditingComment(comment.commentId);
    setEditText(comment.body);
  };

  const cancelEditing = () => {
    setEditingComment(null);
    setEditText('');
  };

  return (
    <div className="comment-section">
      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <div className="comments-list">
        {commentsList.length === 0 ? (
          <p className="no-comments">No comments yet. Be the first to comment!</p>
        ) : (
          commentsList.map(comment => (
            <div key={comment.commentId} className="comment">
              <div className="comment-header">
                <span className="comment-author">{comment.authorDisplayName}</span>
                <span className="comment-date">{formatDate(comment.createdAt)}</span>
                
                {canEditComment(comment) && editingComment !== comment.commentId && (
                  <div className="comment-actions">
                    <button
                      onClick={() => startEditing(comment)}
                      className="comment-action-btn edit"
                      title="Edit comment"
                      aria-label="Edit comment"
                    >
                      <PencilIcon width={14} height={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteComment(comment.commentId)}
                      className="comment-action-btn delete"
                      title="Delete comment"
                      aria-label="Delete comment"
                      disabled={loading}
                    >
                      <TrashIcon width={14} height={14} />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="comment-body">
                {editingComment === comment.commentId ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleEditComment(comment.commentId);
                    }}
                    className="comment-edit-form"
                  >
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="comment-edit-textarea"
                      maxLength="1000"
                      required
                    />
                    <div className="comment-edit-actions">
                      <button
                        type="button"
                        onClick={cancelEditing}
                        className="btn btn-secondary btn-sm"
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary btn-sm"
                        disabled={loading || !editText.trim()}
                      >
                        {loading ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div 
                    className="comment-text content-preview"
                    dangerouslySetInnerHTML={{ __html: renderCommentBody(comment.body) }}
                  />
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {isAuthenticated && (
        <form onSubmit={handleAddComment} className="comment-form">
          <div className="comment-input-group">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="comment-textarea"
              placeholder="Add a comment..."
              maxLength="1000"
              rows="3"
              required
            />
            <div className="comment-form-footer">
              <div className="character-counter">
                {newComment.length}/1000 characters
              </div>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={loading || !newComment.trim()}
              >
                {loading ? 'Posting...' : 'Post Comment'}
              </button>
            </div>
          </div>
        </form>
      )}

      {!isAuthenticated && (
        <div className="comment-auth-prompt">
          <p>
            <Link to={ROUTES.LOGIN}>Sign in</Link> or <Link to={ROUTES.REGISTER}>create an account</Link> to comment.
          </p>
        </div>
      )}
    </div>
  );
}

export default CommentSection;